import { useEffect, useState, useCallback, useRef } from "react";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SectionCard, DataTable, EmptyState, ActionButton, StatusBadge, Pill, LoadingState } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import CommProvenanceTimeline from "@/components/CommProvenanceTimeline";

const TEAM_LABELS = {
  us_tax: "U.S. Tax Team", asian_tax: "Asian Tax Team", spanish_tax: "Spanish Tax Team",
  sales: "Sales", client_success: "Client Success", production: "Production",
  finance: "Finance", management: "Management", administration: "Administration",
};
const teamLabel = (k) => TEAM_LABELS[k] || (k ? k.replace(/_/g, " ") : "Unassigned");

const TONES = {
  draft: "neutral", generated: "info", sent: "info", viewed: "purple", signed: "success",
  declined: "danger", active: "success", cancelled: "neutral", superseded: "neutral",
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

function MiniCard({ label, value, icon, tone = "slate" }) {
  const Icon = Icons[icon] || Icons.Circle;
  const tones = { slate: "text-slate-800", emerald: "text-emerald-600", blue: "text-blue-600", rose: "text-rose-600", violet: "text-violet-600" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`el-kpi-${label.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className={`text-2xl font-heading font-semibold mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function EngagementLetters({ clientId, reload, defaultDetailId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [detailOpenEditor, setDetailOpenEditor] = useState(false);
  const [deleteDraftId, setDeleteDraftId] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/engagements", { params: { client_id: clientId } });
      setRows((data || []).filter((e) => e.template_id));
    } catch (e) { console.warn("Failed to load engagements", e); } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  // Open a specific letter when deeplinked from the Engagements tab
  useEffect(() => {
    if (defaultDetailId) setDetailId(defaultDetailId);
  }, [defaultDetailId]);

  // After a withdraw-and-revise: refresh the list, close the old detail, open the replacement
  const handleWithdrawnRevised = useCallback((newId) => {
    load();
    reload?.();
    setDetailId(null);
    setTimeout(() => { setDetailOpenEditor(false); setDetailId(newId); }, 80);
  }, [load, reload]);

  // Build supersededBy map: original_id → replacement_engagement_number (client-side derivation)
  const supersededBy = {};
  rows.forEach((r) => { if (r.supersedes) supersededBy[r.supersedes] = r.engagement_number; });

  // Row styling: historical rows are visually muted
  const getRowClass = (r) =>
    ["superseded", "cancelled"].includes(r.engagement_status)
      ? "opacity-60 bg-slate-50/40 text-slate-400"
      : "";

  // Table actions: inline reminder (no detail open needed)
  const handleRemindInline = useCallback(async (eid) => {
    try {
      await api.post(`/engagements/${eid}/remind`, { status: "manual", base_url: window.location.origin });
      toast.success("Reminder sent");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reminder failed"); }
  }, []);

  // Delete draft (void): governed cancel for never-sent letters
  const handleDeleteDraftConfirm = useCallback(async () => {
    if (!deleteDraftId) return;
    setDeleteBusy(true);
    try {
      await api.post(`/engagements/${deleteDraftId}/void`);
      toast.success("Draft discarded");
      setDeleteDraftId(null);
      load();
      reload?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Discard failed"); }
    finally { setDeleteBusy(false); }
  }, [deleteDraftId, load, reload]);

  // Download: signed doc > unsigned doc
  const handleDownload = useCallback((row) => {
    const docId = row.signed_document_id || row.unsigned_document_id;
    if (!docId) return toast.error("No document available");
    window.open(`${API}/files/${docId}/download?auth=${encodeURIComponent(getToken() || "")}`, "_blank");
  }, []);

  const counts = (s) => rows.filter((r) => r.engagement_status === s).length;
  const columns = [
    { key: "engagement_number", label: "Engagement #" },
    { key: "template_name", label: "Template" },
    { key: "primary_team_id", label: "Assigned Team" },
    { key: "engagement_status", label: "Status" },
    { key: "sent_at", label: "Sent" },
    { key: "signed_at", label: "Signed" },
    { key: "actions", label: "", align: "right", cellClassName: "w-10 py-1" },
  ];
  const renderCell = (r, c) => {
    if (c.key === "engagement_status") return (
      <div className="flex flex-col gap-0.5">
        <StatusBadge status={r.engagement_status} map={TONES} />
        {r.supersedes && <span className="text-[10px] text-slate-400">Supersedes {r.supersedes_engagement_number || r.supersedes.slice(0, 8)}</span>}
      </div>
    );
    if (c.key === "engagement_number") return (
      <div className="flex flex-col gap-0.5">
        <span className="font-mono text-xs">{r.engagement_number}</span>
        {supersededBy[r.id] && <span className="text-[10px] text-amber-500">Superseded by {supersededBy[r.id]}</span>}
      </div>
    );
    if (c.key === "primary_team_id") return <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{teamLabel(r.primary_team_id || r.assigned_team_id)}</span>;
    if (c.key === "sent_at" || c.key === "signed_at") return <span className="text-slate-500">{fmtDate(r[c.key])}</span>;
    if (c.key === "actions") return (
      <RowActionsMenu
        row={r}
        onView={(id) => { setDetailOpenEditor(false); setDetailId(id); }}
        onEditDraft={(id) => { setDetailOpenEditor(true); setDetailId(id); }}
        onDeleteDraft={(id) => setDeleteDraftId(id)}
        onDownload={handleDownload}
        onRemind={handleRemindInline}
        onWithdrawRevise={(id) => { setDetailOpenEditor(false); setDetailId(id); }}
      />
    );
    return <span>{r[c.key] || "—"}</span>;
  };

  return (
    <div data-testid="engagement-letters-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
        <MiniCard label="Draft" value={counts("generated") + counts("draft")} icon="FileText" />
        <MiniCard label="Sent" value={counts("sent")} icon="Send" tone="blue" />
        <MiniCard label="Viewed" value={counts("viewed")} icon="Eye" tone="violet" />
        <MiniCard label="Signed" value={counts("signed") + counts("active")} icon="FileCheck" tone="emerald" />
        <MiniCard label="Declined" value={counts("declined") + counts("cancelled")} icon="XCircle" tone="rose" />
      </div>
      <SectionCard title="Engagement Letters" icon="FileCheck">
        {loading ? <LoadingState /> : (
          <DataTable columns={columns} rows={rows} renderCell={renderCell} testId="engagement-letters-table"
            getRowClass={getRowClass}
            empty={<EmptyState icon="FileCheck" title="No engagement letters yet"
              description="Engagement letters are auto-generated when an estimate is accepted." />} />
        )}
      </SectionCard>
      <Binder onRowClick={(id) => setDetailId(id)} />

      {/* Delete Draft confirmation */}
      <AlertDialog open={!!deleteDraftId} onOpenChange={(o) => !o && setDeleteDraftId(null)}>
        <AlertDialogContent data-testid="delete-draft-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Draft Engagement Letter?</AlertDialogTitle>
            <AlertDialogDescription>
              This draft has never been sent to the client. It will be marked as cancelled and removed
              from active workflows. The record is preserved in audit history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-draft-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDraftConfirm} disabled={deleteBusy}
              className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-draft-confirm">
              {deleteBusy ? "Discarding…" : "Discard Draft"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="mt-6">
        <CommProvenanceTimeline clientId={clientId} title="Engagement Letter Provenance"
          scopeLabel="engagement letters" />
      </div>
      {detailId && (
        <EngagementDetail
          id={detailId}
          openEditor={detailOpenEditor}
          onClose={() => { setDetailId(null); setDetailOpenEditor(false); }}
          onChanged={() => { load(); reload?.(); }}
          onWithdrawnRevised={handleWithdrawnRevised}
        />
      )}
    </div>
  );
}

function LetterDraftEditor({ eng, onSaved, onClose }) {
  const [mode, setMode] = useState("edit");
  const [previewHtml, setPreviewHtml] = useState(sanitizeHtml(eng.letter_body || ""));
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [versions, setVersions] = useState(null);
  const editorRef = useRef(null);

  // Set editor content once on mount
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = sanitizeHtml(eng.letter_body || "");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load version history
  useEffect(() => {
    api.get(`/engagements/${eng.id}/letter/versions`)
      .then(({ data }) => setVersions(data))
      .catch(() => {});
  }, [eng.id]);

  const switchToPreview = () => {
    if (editorRef.current) setPreviewHtml(editorRef.current.innerHTML);
    setMode("preview");
  };

  const saveDraft = async () => {
    if (!editorRef.current) return;
    setSaving(true);
    try {
      await api.patch(`/engagements/${eng.id}/letter`, { body: editorRef.current.innerHTML });
      toast.success("Draft saved");
      const { data } = await api.get(`/engagements/${eng.id}/letter/versions`);
      setVersions(data);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const sendLetter = async () => {
    setSending(true);
    // Auto-save current draft before sending
    if (mode === "edit" && editorRef.current) {
      try {
        await api.patch(`/engagements/${eng.id}/letter`, { body: editorRef.current.innerHTML });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Auto-save failed — please save before sending");
        setSending(false);
        return;
      }
    }
    try {
      await api.post(`/engagements/${eng.id}/send`, { base_url: window.location.origin });
      toast.success("Engagement letter sent for signature");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      setSending(false);
    }
  };

  const templateName = eng.template_name || (eng.letter_provenance || {}).template_id || "Template";
  const latestVer = versions?.[versions.length - 1];

  return (
    <div className="flex flex-col gap-3" data-testid="letter-draft-editor">
      {/* Mode toggle + hint */}
      <div className="flex items-center justify-between">
        <div className="flex">
          <button
            onClick={() => setMode("edit")}
            className={cn("text-xs px-3 py-1.5 rounded-l-md border font-medium transition-colors",
              mode === "edit" ? "bg-[#171B2E] text-white border-[#171B2E]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}
            data-testid="editor-mode-edit"
          >Edit</button>
          <button
            onClick={switchToPreview}
            className={cn("text-xs px-3 py-1.5 rounded-r-md border border-l-0 font-medium transition-colors",
              mode === "preview" ? "bg-[#171B2E] text-white border-[#171B2E]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}
            data-testid="editor-mode-preview"
          >Preview</button>
        </div>
        {mode === "edit" && (
          <span className="text-[11px] text-slate-400">Ctrl+B / Cmd+B = bold · Ctrl+I / Cmd+I = italic</span>
        )}
      </div>

      {/* Editor — always mounted so cursor is preserved when toggling to preview */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        style={{ display: mode === "edit" ? "block" : "none" }}
        className="min-h-[40vh] max-h-[48vh] overflow-y-auto tf-scroll border border-slate-200 rounded-lg p-5 text-sm text-slate-700 leading-relaxed bg-white outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 transition-all"
        data-testid="letter-editor-content"
      />
      {/* Preview — rendered HTML */}
      {mode === "preview" && (
        <div
          className="min-h-[40vh] max-h-[48vh] overflow-y-auto tf-scroll border border-slate-100 rounded-lg p-5 bg-white text-sm text-slate-700 leading-relaxed"
          data-testid="letter-preview-content"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml) }}
        />
      )}

      {/* Provenance line */}
      <div className="flex items-center justify-between text-[11px] text-slate-400 border-t pt-2">
        <span>Generated from: <span className="font-medium text-slate-600">{templateName}</span></span>
        {latestVer && (
          <span>
            {latestVer.source === "ai_generated"
              ? "v1 · AI Generated"
              : `v${latestVer.version} · Edited by ${latestVer.created_by_name || "staff"}`}
            {" · "}{fmtDate(latestVer.created_at)}
          </span>
        )}
      </div>

      {/* Version history */}
      {versions && versions.length > 0 && (
        <div className="border border-slate-100 rounded-lg overflow-hidden" data-testid="letter-version-history">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 px-3 py-1.5 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5">
            <Icons.History className="h-3 w-3" />Version History
          </div>
          <div className="divide-y divide-slate-50 max-h-28 overflow-y-auto">
            {versions.slice().reverse().map((v) => (
              <div key={v.version} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`version-row-${v.version}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-slate-400">v{v.version}</span>
                  <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium",
                    v.source === "ai_generated" ? "bg-indigo-50 text-indigo-700" : "bg-emerald-50 text-emerald-700")}>
                    {v.source === "ai_generated" ? "AI Generated" : "Staff Edit"}
                  </span>
                  {v.note && <span className="text-slate-400 truncate max-w-[180px]">{v.note}</span>}
                </div>
                <span className="text-slate-400 shrink-0 text-[11px]">{v.created_by_name || "system"} · {fmtDate(v.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action footer */}
      <div className="flex items-center justify-between gap-2 border-t pt-3" data-testid="editor-footer">
        <ActionButton variant="secondary" onClick={onClose} data-testid="editor-close-btn">
          Back to Letter
        </ActionButton>
        <div className="flex items-center gap-2">
          <ActionButton variant="secondary" icon="Save" loading={saving} onClick={saveDraft} data-testid="editor-save-draft">
            Save Draft
          </ActionButton>
          <ActionButton icon="Send" loading={sending} onClick={sendLetter} data-testid="editor-send-letter">
            Send Engagement Letter
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

function RowActionsMenu({ row, onView, onEditDraft, onDeleteDraft, onDownload, onRemind, onWithdrawRevise }) {
  const s = row.engagement_status;
  const isDraft      = ["generated", "draft"].includes(s);
  const isSentViewed = ["sent", "viewed"].includes(s);
  const isExecuted   = ["signed", "active"].includes(s);
  const isHistorical = ["superseded", "cancelled"].includes(s);
  const hasDoc       = row.signed_document_id || row.unsigned_document_id;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
          onClick={(e) => e.stopPropagation()}
          data-testid={`el-actions-btn-${row.id}`}
          aria-label="Actions"
        >
          <Icons.MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>

        <DropdownMenuItem onClick={() => onView(row.id)} data-testid={`el-action-view-${row.id}`}>
          <Icons.Eye className="h-3.5 w-3.5 mr-2 flex-shrink-0" />View Letter
        </DropdownMenuItem>

        {isDraft && (
          <DropdownMenuItem onClick={() => onEditDraft(row.id)} data-testid={`el-action-edit-${row.id}`}>
            <Icons.Pencil className="h-3.5 w-3.5 mr-2 flex-shrink-0" />Edit Draft
          </DropdownMenuItem>
        )}

        {hasDoc && (
          <DropdownMenuItem onClick={() => onDownload(row)} data-testid={`el-action-download-${row.id}`}>
            <Icons.Download className="h-3.5 w-3.5 mr-2 flex-shrink-0" />Download
          </DropdownMenuItem>
        )}

        {isSentViewed && (
          <DropdownMenuItem onClick={() => onRemind(row.id)} data-testid={`el-action-remind-${row.id}`}>
            <Icons.BellRing className="h-3.5 w-3.5 mr-2 flex-shrink-0" />Send Reminder
          </DropdownMenuItem>
        )}

        {(isDraft || isSentViewed) && <DropdownMenuSeparator />}

        {isDraft && (
          <DropdownMenuItem
            onClick={() => onDeleteDraft(row.id)}
            className="text-rose-600 focus:text-rose-600 focus:bg-rose-50"
            data-testid={`el-action-delete-${row.id}`}
          >
            <Icons.Trash2 className="h-3.5 w-3.5 mr-2 flex-shrink-0" />Delete Draft
          </DropdownMenuItem>
        )}

        {isSentViewed && (
          <DropdownMenuItem
            onClick={() => onWithdrawRevise(row.id)}
            className="text-amber-600 focus:text-amber-600 focus:bg-amber-50"
            data-testid={`el-action-withdraw-${row.id}`}
          >
            <Icons.RotateCcw className="h-3.5 w-3.5 mr-2 flex-shrink-0" />Withdraw &amp; Revise
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EngagementDetail({ id, onClose, onChanged, onWithdrawnRevised, openEditor }) {
  const [eng, setEng] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  // Withdraw & Revise mode-selection dialog
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawMode, setWithdrawMode] = useState(null); // "revise" | "rebuild"
  // Repair This Draft confirmation
  const [confirmRepair, setConfirmRepair] = useState(false);
  // Record Client Acceptance dialog
  const [showAcceptanceDialog, setShowAcceptanceDialog] = useState(false);

  const load = useCallback(async () => { const { data } = await api.get(`/engagements/${id}`); setEng(data); }, [id]);
  useEffect(() => { load(); }, [load]);
  // Auto-open editor when the detail was opened via "Edit Draft" in the table actions menu
  useEffect(() => { if (openEditor && eng) setShowEditor(true); }, [openEditor, eng]);
  // Initialise withdrawMode default when dialog opens or revision_context changes
  useEffect(() => {
    if (!eng) return;
    const mismatch = eng.revision_context?.scope_mismatch;
    setWithdrawMode(mismatch ? "rebuild" : "revise");
  }, [eng, showWithdrawDialog]);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); onChanged(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); } finally { setBusy(false); } };
  const copyLink = () => {
    if (!eng.sign_token) return toast.error("Send the letter first to generate a link");
    navigator.clipboard.writeText(`${window.location.origin}/e/${eng.sign_token}`);
    toast.success("Signing link copied");
  };

  const handleWithdrawAndRevise = async () => {
    setShowWithdrawDialog(false);
    setBusy(true);
    try {
      const { data: replacement } = await api.post(`/engagements/${id}/withdraw-and-revise`, { mode: withdrawMode });
      const modeLabel = withdrawMode === "rebuild" ? "rebuilt from accepted services" : "copied for revision";
      toast.success(`Letter withdrawn — replacement draft ${modeLabel}`);
      onChanged();
      onWithdrawnRevised?.(replacement.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Withdraw failed");
    } finally { setBusy(false); }
  };

  const handleRebuildFromScope = async () => {
    setConfirmRepair(false);
    setBusy(true);
    try {
      await api.post(`/engagements/${id}/rebuild-from-scope`);
      toast.success("Draft rebuilt from accepted commercial scope — review before sending");
      load();
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rebuild failed");
    } finally { setBusy(false); }
  };

  if (!eng) return <Dialog open onOpenChange={onClose}><DialogContent><LoadingState /></DialogContent></Dialog>;
  const locked = eng.locked || ["signed", "active", "superseded", "cancelled"].includes(eng.engagement_status);
  const canSend = ["draft", "generated"].includes(eng.engagement_status);
  const canWithdrawRevise = ["sent", "viewed"].includes(eng.engagement_status) && !eng.locked && !["signed", "active"].includes(eng.engagement_status);
  const revCtx = eng.revision_context || {};
  const scopeMismatch = Boolean(revCtx.scope_mismatch);
  const canRecordAcceptance = !locked
    && !scopeMismatch
    && Boolean(eng.letter_body)
    && ["generated", "sent", "viewed"].includes(eng.engagement_status);
  // Repair This Draft: replacement draft (supersedes set) that is unsent and has a scope mismatch
  const canRepairDraft = Boolean(
    eng.supersedes &&
    ["draft", "generated"].includes(eng.engagement_status) &&
    !eng.locked &&
    scopeMismatch
  );

  return (
    <Dialog open onOpenChange={onClose}>
      {/* Viewport-bounded modal: flex column with constrained height so the sticky footer
          (lifecycle action buttons) is always reachable regardless of letter length,
          Allison review length, or browser zoom. Single scroll region inside. */}
      <DialogContent className={cn(showEditor ? "max-w-4xl" : "max-w-2xl", "flex flex-col max-h-[90vh] overflow-hidden")} data-testid="engagement-detail">
        {/* Fixed header — never scrolled away */}
        <DialogHeader className="shrink-0 pb-1">
          <DialogTitle className="font-heading flex items-center gap-3">{eng.engagement_number}<StatusBadge status={eng.engagement_status} map={TONES} />{locked && <Pill tone="neutral"><Icons.Lock className="h-3 w-3 mr-1 inline" />Locked</Pill>}</DialogTitle>
          <DialogDescription>{eng.template_name} · {eng.client?.full_name}</DialogDescription>
        </DialogHeader>

        {showEditor ? (
          <LetterDraftEditor
            eng={eng}
            onSaved={() => { load(); onChanged(); }}
            onClose={() => setShowEditor(false)}
          />
        ) : (
          <>
            {/* Single intentional scroll region — no nested scrollers below this level */}
            <div className="flex-1 overflow-y-auto tf-scroll min-h-0 space-y-3 pr-1" data-testid="engagement-detail-scroll">
              <div className="border border-slate-100 rounded-lg p-5 bg-white text-sm text-slate-700 leading-relaxed"
                data-testid="engagement-preview" dangerouslySetInnerHTML={{ __html: sanitizeHtml(eng.letter_body) }} />
              {eng.supersedes && (
                <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700 flex items-center gap-2" data-testid="engagement-supersedes-notice">
                  <Icons.History className="h-3.5 w-3.5 flex-shrink-0" />
                  Revised version — supersedes {eng.supersedes_engagement_number || eng.supersedes}
                </div>
              )}
              {/* Scope mismatch warning on replacement draft */}
              {canRepairDraft && (
                <div className="px-3 py-2.5 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-start gap-2" data-testid="engagement-scope-mismatch-warning">
                  <Icons.AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Scope mismatch detected.</strong> This draft does not match the accepted services
                    ({revCtx.canonical_service_names?.join(", ") || "accepted scope"}).
                    Use <strong>Repair This Draft</strong> to rebuild it from the correct commercial scope before sending.
                  </span>
                </div>
              )}
              <TeamAssignmentPanel engagementId={id} onChanged={() => { load(); onChanged(); }} />
              {locked && ["signed", "active"].includes(eng.engagement_status) && (
                eng.is_staff_recorded_acceptance
                  ? <StaffAcceptanceSummaryPanel eng={eng} />
                  : <ExecutionEvidencePanel engagementId={id} />
              )}
              <BillingSchedulePanel engagementId={id} />
              <AllisonReviewPanel engagementId={id} paymentGate={eng.production_gate} />
            </div>

            {/* Sticky footer — lifecycle controls always visible, never buried by content */}
            <div className="shrink-0 border-t pt-3 flex items-center justify-end flex-wrap gap-2" data-testid="engagement-detail-footer">
              {/* Repair This Draft — replacement draft with scope mismatch */}
              {canRepairDraft && (
                <ActionButton
                  icon="RefreshCw"
                  variant="danger"
                  loading={busy}
                  onClick={() => setConfirmRepair(true)}
                  data-testid="engagement-repair-draft"
                >
                  Repair This Draft
                </ActionButton>
              )}
              {canSend && !canRepairDraft && <ActionButton icon="Pencil" onClick={() => setShowEditor(true)} data-testid="engagement-edit-draft">Edit Draft</ActionButton>}
              {canSend && !canRepairDraft && <ActionButton icon="Send" loading={busy} onClick={() => act(() => api.post(`/engagements/${id}/send`, { base_url: window.location.origin }), "Engagement sent")} data-testid="engagement-send">Send for Signature</ActionButton>}
              {canSend && canRepairDraft && <ActionButton icon="Pencil" variant="secondary" onClick={() => setShowEditor(true)} data-testid="engagement-edit-draft">Edit Draft</ActionButton>}
              {eng.sign_token && !locked && <ActionButton variant="secondary" icon="Send" loading={busy} onClick={() => act(() => api.post(`/engagements/${id}/send`, { base_url: window.location.origin }), "Resent")} data-testid="engagement-resend">Resend</ActionButton>}
              {eng.sign_token && <ActionButton variant="secondary" icon="Link" onClick={copyLink} data-testid="engagement-copy-link">Copy Sign Link</ActionButton>}
              {["sent", "viewed"].includes(eng.engagement_status) && <ActionButton variant="secondary" icon="BellRing" loading={busy} onClick={() => act(() => api.post(`/engagements/${id}/remind`, { status: "manual", base_url: window.location.origin }), "Reminder sent")} data-testid="engagement-remind">Remind</ActionButton>}
              {canWithdrawRevise && (
                <ActionButton variant="secondary" icon="RotateCcw" loading={busy}
                  onClick={() => setShowWithdrawDialog(true)} data-testid="engagement-withdraw-revise">
                  Withdraw &amp; Revise
                </ActionButton>
              )}
              {canRecordAcceptance && (
                <ActionButton variant="secondary" icon="CheckSquare"
                  onClick={() => setShowAcceptanceDialog(true)}
                  data-testid="engagement-record-acceptance">
                  Record Client Acceptance
                </ActionButton>
              )}
              {eng.signed_document_id && <ActionButton variant="secondary" icon="Download" onClick={() => window.open(`${API}/files/${eng.signed_document_id}/download?auth=${encodeURIComponent(getToken() || "")}`, "_blank")} data-testid="engagement-download-signed">Signed PDF</ActionButton>}
              {!locked && canSend && <ActionButton variant="danger" icon="XCircle" loading={busy} onClick={() => act(() => api.post(`/engagements/${id}/void`), "Draft discarded")} data-testid="engagement-void">Discard Draft</ActionButton>}
              {locked && eng.engagement_status !== "signed" && <ActionButton variant="secondary" icon="Copy" loading={busy} onClick={() => act(() => api.post(`/engagements/${id}/duplicate`), "Superseded")} data-testid="engagement-duplicate">Supersede</ActionButton>}
            </div>
          </>
        )}

        {/* ── Withdraw & Revise mode-selection dialog ── */}
        <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
          <DialogContent className="max-w-md" data-testid="withdraw-revise-mode-dialog">
            <DialogHeader>
              <DialogTitle>Withdraw &amp; Revise Engagement Letter</DialogTitle>
              <DialogDescription>
                Withdrawing will disable the current signing link. The original is preserved in audit history.
                Choose how the replacement draft should be created.
              </DialogDescription>
            </DialogHeader>

            {/* Scope mismatch warning */}
            {scopeMismatch && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-xs text-rose-700 flex items-start gap-2" data-testid="withdraw-scope-mismatch-warning">
                <Icons.AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Scope mismatch.</strong> The current letter uses{" "}
                  <em>{revCtx.prior_template_name || revCtx.prior_template_key}</em>, but the accepted
                  services resolve to{" "}
                  <em>{revCtx.canonical_template_name || revCtx.canonical_template_key}</em>.{" "}
                  Rebuild is required to produce a legally consistent replacement.
                </span>
              </div>
            )}

            {/* Mode selection */}
            <div className="space-y-2 py-1" data-testid="withdraw-mode-options">
              {/* Rebuild option */}
              <label
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                  withdrawMode === "rebuild"
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-slate-200 hover:bg-slate-50"
                )}
                data-testid="withdraw-mode-rebuild"
              >
                <input
                  type="radio"
                  name="withdrawMode"
                  value="rebuild"
                  checked={withdrawMode === "rebuild"}
                  onChange={() => setWithdrawMode("rebuild")}
                  className="mt-0.5 accent-emerald-600"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-800">Rebuild From Accepted Services</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Generate a fresh replacement from the canonical accepted commercial scope —
                    correct template, title, narrative, and fee section.
                    {scopeMismatch && <span className="ml-1 font-medium text-emerald-700">Recommended</span>}
                  </div>
                </div>
              </label>

              {/* Revise option */}
              <label
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-3 transition-colors",
                  scopeMismatch
                    ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
                    : withdrawMode === "revise"
                    ? "border-blue-500 bg-blue-50 cursor-pointer"
                    : "border-slate-200 hover:bg-slate-50 cursor-pointer"
                )}
                data-testid="withdraw-mode-revise"
              >
                <input
                  type="radio"
                  name="withdrawMode"
                  value="revise"
                  checked={withdrawMode === "revise"}
                  disabled={scopeMismatch}
                  onChange={() => !scopeMismatch && setWithdrawMode("revise")}
                  className="mt-0.5 accent-blue-600"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-800">Revise Existing Letter</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Copy the sent letter body for text-only wording corrections.
                    {scopeMismatch && (
                      <span className="ml-1 text-rose-600 font-medium">
                        Not available — service mismatch detected.
                      </span>
                    )}
                  </div>
                </div>
              </label>
            </div>

            <DialogFooter className="gap-2">
              <button
                onClick={() => setShowWithdrawDialog(false)}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                data-testid="withdraw-revise-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleWithdrawAndRevise}
                disabled={busy || !withdrawMode}
                className={cn(
                  "px-4 py-2 text-sm rounded-lg text-white font-medium transition-colors disabled:opacity-50",
                  withdrawMode === "rebuild"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-amber-600 hover:bg-amber-700"
                )}
                data-testid="withdraw-revise-confirm"
              >
                {withdrawMode === "rebuild" ? "Withdraw & Rebuild" : "Withdraw & Revise"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Repair This Draft confirmation ── */}
        <AlertDialog open={confirmRepair} onOpenChange={setConfirmRepair}>
          <AlertDialogContent data-testid="repair-draft-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Repair This Draft</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">
                  This draft does not match the accepted services.
                  Rebuilding will replace the current letter body, template, title, and service snapshot
                  with content generated from the canonical accepted commercial scope
                  ({revCtx.canonical_service_names?.join(", ") || "accepted services"}).
                </span>
                <span className="block text-slate-500">
                  The previous draft body will be preserved in version history. The original sent letter
                  remains unchanged. Staff review is required before the repaired draft can be sent.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="repair-draft-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRebuildFromScope}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="repair-draft-confirm"
              >
                Rebuild From Accepted Scope
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {/* ── Record Client Acceptance dialog ── */}
        <RecordAcceptanceDialog
          open={showAcceptanceDialog}
          onOpenChange={setShowAcceptanceDialog}
          engagementId={id}
          invoiceId={(eng.production_gate || {}).invoice_id || null}
          onAccepted={() => { load(); onChanged(); }}
          data-testid="record-acceptance-dialog-root"
        />
      </DialogContent>
    </Dialog>
  );
}

// ── Record Client Acceptance Dialog ────────────────────────────────────────────
const ACCEPTANCE_METHODS = [
  { value: "payment_received",   label: "Payment received" },
  { value: "email",              label: "Client confirmed by email" },
  { value: "conversation",       label: "Client confirmed by conversation" },
  { value: "verbal",             label: "Client confirmed verbally" },
  { value: "external_signature", label: "Signed outside Tax Flow" },
  { value: "historical_record",  label: "Historical / prior acceptance" },
  { value: "other",              label: "Other" },
];

function RecordAcceptanceDialog({ open, onOpenChange, engagementId, invoiceId, onAccepted }) {
  const [method, setMethod]       = useState("");
  const [note, setNote]           = useState("");
  const [effectiveAt, setEffectiveAt] = useState(new Date().toISOString().split("T")[0]);
  const [evidenceRef, setEvidenceRef] = useState("");
  const [busy, setBusy]           = useState(false);
  const [invoice, setInvoice]     = useState(null);

  // Fetch invoice detail when dialog opens and invoiceId is present
  useEffect(() => {
    if (!open) return;
    setMethod("");
    setNote("");
    setEffectiveAt(new Date().toISOString().split("T")[0]);
    setEvidenceRef("");
    setInvoice(null);
    if (!invoiceId) return;
    api.get(`/invoices/${invoiceId}`).then(({ data }) => {
      setInvoice(data);
      const isPaid = data?.status === "paid" || (data?.balance != null && data.balance <= 0);
      if (isPaid) setMethod("payment_received");
    }).catch(() => {});
  }, [open, invoiceId]);

  const invoicePaid = invoice && (invoice.status === "paid" || (invoice.balance != null && invoice.balance <= 0));

  const canSubmit = method && note.trim() && effectiveAt;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await api.post(`/engagements/${engagementId}/record-client-acceptance`, {
        acceptance_method: method,
        acceptance_note: note.trim(),
        effective_at: effectiveAt,
        evidence_reference: evidenceRef.trim() || undefined,
      });
      toast.success("Client acceptance recorded");
      onAccepted();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record acceptance");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="record-acceptance-dialog">
        <DialogHeader>
          <DialogTitle>Record Client Acceptance</DialogTitle>
          <DialogDescription>
            Document how the client accepted this engagement. This is a staff-recorded
            acceptance and is <strong>not</strong> a portal e-signature.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Invoice status — shown when linked and loaded */}
          {invoiceId && invoice && (
            <div className={`rounded-lg border px-3 py-2 text-sm ${invoicePaid ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}
              data-testid="acceptance-invoice-status">
              <span className="font-medium">{invoice.invoice_number || invoiceId}</span>
              {invoice.total != null && <span className="ml-2">&middot; ${invoice.total?.toFixed(2)}</span>}
              {invoice.amount_paid != null && <span className="ml-1 text-xs">(paid ${invoice.amount_paid?.toFixed(2)})</span>}
              {invoicePaid
                ? <span className="ml-2 font-medium">· Balance $0</span>
                : <span className="ml-2">· Balance ${(invoice.balance ?? invoice.total - invoice.amount_paid)?.toFixed(2)} outstanding</span>}
            </div>
          )}
          {invoiceId && !invoice && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 animate-pulse">
              Loading invoice…
            </div>
          )}

          {/* Acceptance method */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">How was acceptance obtained? *</label>
            <select
              value={method}
              onChange={e => setMethod(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="acceptance-method-select">
              <option value="">Select method…</option>
              {ACCEPTANCE_METHODS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Effective date */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">Effective acceptance date *</label>
            <input type="date" value={effectiveAt} onChange={e => setEffectiveAt(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="acceptance-effective-date" />
          </div>

          {/* Governance note */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">Governance note *</label>
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
              placeholder="Briefly describe the acceptance evidence (required)…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              data-testid="acceptance-note-input" />
          </div>

          {/* Evidence reference */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-500 text-xs">Supporting reference (optional — email thread, receipt, contract ref…)</label>
            <input type="text" value={evidenceRef} onChange={e => setEvidenceRef(e.target.value)}
              placeholder="e.g. Email from client@example.com on Aug 10"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="acceptance-evidence-ref" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} data-testid="acceptance-cancel"
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={busy || !canSubmit}
            data-testid="acceptance-confirm"
            className="px-4 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-50">
            {busy ? "Recording…" : "Record Client Acceptance"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Staff Acceptance Summary Panel ─────────────────────────────────────────────
// Shown instead of ExecutionEvidencePanel when is_staff_recorded_acceptance = true.
// Never says "Client E-Signed" — clearly attributes to staff.
const ACCEPTANCE_METHOD_LABELS = {
  payment_received:   "Payment received",
  email:              "Client confirmed by email",
  conversation:       "Client confirmed by conversation",
  verbal:             "Client confirmed verbally",
  external_signature: "Signed outside Tax Flow",
  historical_record:  "Historical / prior acceptance",
  other:              "Other",
};

function StaffAcceptanceSummaryPanel({ eng }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="staff-acceptance-panel">
      <div className="flex items-center gap-2">
        <span className="text-emerald-700 font-semibold text-sm">Accepted — recorded by staff</span>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
        {eng.acceptance_recorded_by_name && (
          <>
            <dt className="text-slate-500">Recorded by</dt>
            <dd data-testid="staff-acceptance-recorded-by">{eng.acceptance_recorded_by_name}</dd>
          </>
        )}
        {eng.acceptance_method && (
          <>
            <dt className="text-slate-500">Method</dt>
            <dd data-testid="staff-acceptance-method">
              {ACCEPTANCE_METHOD_LABELS[eng.acceptance_method] || eng.acceptance_method}
            </dd>
          </>
        )}
        {eng.acceptance_effective_at && (
          <>
            <dt className="text-slate-500">Effective</dt>
            <dd data-testid="staff-acceptance-effective-at">
              {new Date(eng.acceptance_effective_at).toLocaleDateString()}
            </dd>
          </>
        )}
        {eng.acceptance_note && (
          <>
            <dt className="text-slate-500 col-span-2 mt-1">Note</dt>
            <dd className="col-span-2 text-slate-700 italic" data-testid="staff-acceptance-note">
              {eng.acceptance_note}
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}

// ── P3: Billing Schedule Panel ─────────────────────────────────────────────────
const STATUS_STYLES = {
  "Complete":          "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Invoice Generated": "bg-blue-50 text-blue-700 border-blue-200",
  "Partially Paid":    "bg-amber-50 text-amber-700 border-amber-200",
  "Ready to Generate": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Waiting for Trigger":"bg-slate-50 text-slate-500 border-slate-200",
  "Not Yet Generated": "bg-amber-50 text-amber-600 border-amber-200",
  "Planned":           "bg-slate-50 text-slate-400 border-slate-100",
  "Cancelled":         "bg-red-50 text-red-600 border-red-200",
};

function BillingSchedulePanel({ engagementId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/engagements/${engagementId}/billing-schedule`);
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [engagementId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async (scheduleKey, label) => {
    setGenerating(scheduleKey);
    try {
      await api.post(`/engagements/${engagementId}/billing-schedule/${scheduleKey}/generate-invoice`);
      toast.success(`Invoice generated for ${label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invoice generation failed");
    } finally { setGenerating(null); }
  };

  if (loading) return null;
  if (!data?.has_policy) return null;

  const fmt = (v) => v != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: data.schedule?.[0]?.currency || "USD" }).format(v) : "—";

  return (
    <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden" data-testid="billing-schedule-panel">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Billing Schedule</span>
          <span className="text-[10px] text-slate-400 font-normal">{data.policy_name}</span>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${data.gate_status === "cleared" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-600 border-amber-200"}`}>
          Gate: {data.gate_status === "cleared" ? "Cleared" : data.gate_status === "awaiting_payment" ? "Awaiting Payment" : data.gate_status || "none"}
        </span>
      </div>
      <div className="divide-y divide-slate-100">
        {(data.schedule || []).map((item) => {
          const statusStyle = STATUS_STYLES[item.display_status] || STATUS_STYLES["Planned"];
          return (
            <div key={item.schedule_key} className="px-4 py-3 flex items-start justify-between gap-3 text-xs" data-testid={`billing-schedule-item-${item.schedule_key}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-800">{item.label}</span>
                  <span className="text-slate-400">#{item.sequence}</span>
                  {item.clears_production_gate && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700">Clears Gate</span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-3 text-slate-500 flex-wrap">
                  <span className="font-mono font-semibold text-slate-700">{fmt(item.amount)}</span>
                  <span className="text-[10px] uppercase tracking-wide">{item.trigger === "on_acceptance" ? "On Acceptance" : item.trigger === "manual" ? "Staff Triggered" : item.trigger}</span>
                  {item.invoice && (
                    <span className="text-slate-400">
                      Invoice <span className="font-mono text-slate-600">{item.invoice.invoice_number}</span>
                      {" · "}{fmt(item.invoice.amount_paid)} paid
                    </span>
                  )}
                  {!item.invoice && item.display_status !== "Complete" && (
                    <span className="text-slate-400 italic">Not yet generated</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-[10px] px-2 py-0.5 rounded border font-medium ${statusStyle}`}>
                  {item.display_status}
                </span>
                {item.is_executable && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        disabled={generating === item.schedule_key}
                        className="text-[11px] px-2.5 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-medium disabled:opacity-50 transition-colors"
                        data-testid={`generate-invoice-btn-${item.schedule_key}`}
                      >
                        {generating === item.schedule_key ? "Generating…" : "Generate Invoice"}
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Generate Invoice — {item.label}</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will create invoice #{item.sequence} for <strong>{fmt(item.amount)}</strong> linked to the <strong>{data.policy_name}</strong> billing schedule.
                          <br /><br />
                          This action is idempotent — running it again will return the same invoice without creating a duplicate.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleGenerate(item.schedule_key, item.label)}
                          className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid={`confirm-generate-invoice-btn-${item.schedule_key}`}
                        >
                          Generate Invoice
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExecutionEvidencePanel({ engagementId }) {
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    (async () => {
      try { const { data } = await api.get(`/engagements/${engagementId}/execution-evidence`); if (live) setEv(data); }
      catch { if (live) setEv(null); } finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [engagementId]);

  const copy = (v) => { if (v) { navigator.clipboard.writeText(v); toast.success("Copied"); } };
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
  const short = (v) => (v ? `${v.slice(0, 22)}…` : "—");

  const openCert = async (download) => {
    try {
      const res = await api.get(`/engagements/${engagementId}/execution-certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (download) { const a = document.createElement("a"); a.href = url; a.download = `${ev.engagement_number}-execution-certificate.pdf`; a.click(); }
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Execution Certificate unavailable"); }
  };
  const signedPdfUrl = ev?.signed_document_id ? `${API}/files/${ev.signed_document_id}/download?auth=${encodeURIComponent(getToken() || "")}` : null;

  const verifyMap = {
    verified: { tone: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: "ShieldCheck", label: "Verified" },
    failed: { tone: "text-rose-700 bg-rose-50 border-rose-200", icon: "ShieldAlert", label: "Verification failed" },
    unavailable_legacy: { tone: "text-amber-700 bg-amber-50 border-amber-200", icon: "ShieldQuestion", label: "Unavailable for legacy execution" },
  };
  const v = verifyMap[ev?.verification?.status] || verifyMap.unavailable_legacy;
  const VIcon = Icons[v.icon] || Icons.Shield;

  const Field = ({ label, value, mono, copyable }) => (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-xs text-slate-700 mt-0.5 flex items-center gap-1 ${mono ? "font-mono" : ""}`}>
        <span className="truncate">{value ?? "—"}</span>
        {copyable && value && <button onClick={() => copy(value)} className="text-slate-400 hover:text-slate-700 shrink-0"><Icons.Copy className="h-3 w-3" /></button>}
      </div>
    </div>
  );

  return (
    <div className="border border-slate-200 bg-slate-50/60 rounded-xl p-4 mt-1" data-testid="execution-evidence-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Icons.FileBadge className="h-4 w-4 text-[#171B2E]" /><span className="font-heading text-sm font-semibold text-[#171B2E]">Execution Evidence</span><span className="text-[10px] text-slate-400">read-only · immutable record</span></div>
        {ev && <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${v.tone}`} data-testid="execution-verify-badge"><VIcon className="h-3.5 w-3.5" />{v.label}</span>}
      </div>
      {loading ? <div className="text-xs text-slate-400 py-2">Loading…</div>
        : !ev ? <div className="text-xs text-slate-400 py-2">Evidence unavailable.</div>
        : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-white rounded-lg border border-slate-100 p-3">
              <Field label="Execution Status" value={ev.execution_status} />
              <Field label="Signer" value={ev.signer_name} />
              <Field label="Executed (local)" value={fmt(ev.executed_at)} />
              <Field label="Master Terms Version" value={ev.master_terms_version ? `v${ev.master_terms_version}` : "—"} />
              <Field label="Execution ID" value={short(ev.execution_id)} mono copyable={false} />
              <Field label="Signature Method" value={ev.signature_method} />
              <Field label="Document Fingerprint" value={ev.document_fingerprint} mono copyable />
              <Field label="Execution Fingerprint" value={ev.execution_fingerprint} mono copyable />
              <Field label="Master Terms Fingerprint" value={ev.master_terms_fingerprint} mono copyable />
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {signedPdfUrl && <a href={signedPdfUrl} target="_blank" rel="noreferrer" data-testid="evidence-view-agreement" className="inline-flex items-center gap-1.5 text-xs bg-[#171B2E] hover:bg-[#222842] text-white rounded-lg px-3 py-1.5"><Icons.FileText className="h-3.5 w-3.5 text-[#F5A623]" />View Executed Agreement</a>}
              {ev.has_certificate && <button onClick={() => openCert(false)} data-testid="evidence-view-cert" className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg px-3 py-1.5"><Icons.Award className="h-3.5 w-3.5" />View Certificate</button>}
              {ev.has_certificate && <button onClick={() => openCert(true)} data-testid="evidence-download-cert" className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg px-3 py-1.5"><Icons.Download className="h-3.5 w-3.5" />Download Certificate</button>}
            </div>
            {ev.verification?.status === "failed" && <div className="text-[11px] text-rose-600 mt-2">The recomputed fingerprint chain does not match the frozen record. Do not rely on this document without investigation.</div>}
          </>
        )}
    </div>
  );
}

function AllisonReviewPanel({ engagementId, paymentGate }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/engagements/${engagementId}/allison-review`); setReview(data); }
    catch { setReview(null); } finally { setLoading(false); }
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);
  const run = async () => { setRunning(true); try { const { data } = await api.post(`/engagements/${engagementId}/allison-review`); setReview(data); toast.success("Allison review updated"); } catch { toast.error("Review failed"); } finally { setRunning(false); } };
  const decide = async (recId, decision) => {
    try { await api.post(`/engagements/${engagementId}/allison-review/${recId}/decision`, { decision }); toast.success(`Marked ${decision}`); load(); }
    catch { toast.error("Failed"); }
  };
  const readyTone = review?.readiness_status === "Ready for Production" ? "text-emerald-600" : "text-amber-600";
  return (
    <div className="border border-fuchsia-100 bg-fuchsia-50/40 rounded-xl p-4 mt-1" data-testid="allison-review-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2"><Icons.Sparkles className="h-4 w-4 text-fuchsia-500" /><span className="font-heading text-sm font-semibold text-[#171B2E]">Allison Engagement Review</span><span className="text-[10px] text-fuchsia-400">advisory · internal only</span></div>
        <button onClick={run} disabled={running} data-testid="allison-review-run" className="inline-flex items-center gap-1 text-xs text-fuchsia-700 hover:text-fuchsia-800 disabled:opacity-60">{running ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.RefreshCw className="h-3.5 w-3.5" />}{review ? "Re-run" : "Run review"}</button>
      </div>
      {paymentGate?.required && (
        <div className={cn("text-xs mb-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full", paymentGate.status === "awaiting_payment" ? "text-amber-700 bg-amber-100" : "text-emerald-700 bg-emerald-50")} data-testid="payment-gate-badge">
          <Icons.CreditCard className="h-3.5 w-3.5" />{paymentGate.status === "awaiting_payment" ? "Production blocked — awaiting payment" : "Payment cleared"}
        </div>
      )}
      {loading ? <div className="text-xs text-slate-400 py-2">Loading…</div>
        : !review ? <div className="text-xs text-slate-400 py-2">No review yet — click "Run review".</div>
        : (
          <div className="space-y-2.5">
            <div className="flex flex-wrap gap-3 text-xs">
              <span>Readiness <b className={readyTone}>{review.readiness_score}% · {review.readiness_status}</b></span>
              <span>Complexity <b className="capitalize">{review.complexity}</b></span>
              <span>Risk <b className="capitalize">{review.risk_level}</b></span>
              <span>Reviewer <b>{review.recommended_reviewer_level}</b></span>
            </div>
            {review.narrative && <p className="text-xs text-slate-600 whitespace-pre-wrap max-h-32 overflow-y-auto bg-white/70 rounded-lg p-2 border border-fuchsia-100">{review.narrative}</p>}
            {(review.missing_documents?.length > 0 || review.missing_information?.length > 0) && (
              <div className="text-xs text-slate-500">
                {review.missing_information?.length > 0 && <div><span className="font-semibold">Missing info:</span> {review.missing_information.join(", ")}</div>}
                {review.missing_documents?.length > 0 && <div><span className="font-semibold">Missing docs:</span> {review.missing_documents.join(", ")}</div>}
              </div>
            )}
            {review.recommendations?.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-[11px] font-semibold text-slate-400 uppercase">Recommendations (staff decides)</div>
                {review.recommendations.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 bg-white rounded-lg border border-slate-100 px-2.5 py-1.5" data-testid={`allison-rec-${r.id}`}>
                    <div className="min-w-0"><div className="text-xs font-medium text-[#171B2E] truncate">{r.title}</div><div className="text-[10px] text-slate-400">{r.detail}</div></div>
                    <div className="flex items-center gap-1 shrink-0">
                      {r.decision ? <span className="text-[10px] text-emerald-600 capitalize">{r.decision}</span> : <>
                        <button onClick={() => decide(r.id, "accept")} data-testid={`allison-rec-accept-${r.id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100">Accept</button>
                        <button onClick={() => decide(r.id, "ignore")} className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-500 hover:bg-slate-200">Ignore</button>
                      </>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10px] text-fuchsia-400">{review.source === "llm" ? "Generated by Allison (Claude)" : "Allison heuristic"}</div>
          </div>
        )}
    </div>
  );
}

function TeamAssignmentPanel({ engagementId, onChanged }) {
  const { user } = useAuth();
  const isMgmt = ["administrator", "partner", "manager"].includes(user?.role);
  const [data, setData] = useState(null);
  const [teamKey, setTeamKey] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/engagements/${engagementId}/team-recommendation`);
      setData(data); setTeamKey(data.current?.primary_team_id || data.recommendation?.team_key || "");
    } catch (e) { console.warn("Failed to load team recommendation", e); }
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);

  const reassign = async () => {
    if (!reason.trim()) return toast.error("A reason is required for reassignment");
    setBusy(true);
    try {
      await api.post(`/engagements/${engagementId}/assign-team`, { team_key: teamKey, reason });
      toast.success("Team reassigned"); setReason(""); load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reassign failed"); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const cur = data.current || {};
  const rec = data.recommendation || {};
  const ctx = data.client_context || {};
  const recMatchesCurrent = rec.team_key === cur.primary_team_id;

  return (
    <div className="mt-4 border border-slate-200 rounded-xl p-4 bg-slate-50/60" data-testid="engagement-team-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Icons.Users className="h-4 w-4 text-indigo-600" />Work Assignment</div>
        {data.history?.length > 0 && <button onClick={() => setShowHistory((s) => !s)} data-testid="team-history-toggle" className="text-xs text-slate-500 hover:text-slate-800">{showHistory ? "Hide" : "History"} ({data.history.length})</button>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Currently Assigned</div>
          <div className="font-medium text-slate-800 mt-0.5" data-testid="team-current">{teamLabel(cur.primary_team_id)}</div>
          <div className="text-xs text-slate-500 mt-1">{cur.assignment_source === "manual_override" ? "Manual override" : "Auto-routed"} · {cur.assignment_reason || "—"}</div>
        </div>
        <div className={`border rounded-lg p-3 ${recMatchesCurrent ? "bg-white border-slate-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Engine Recommendation</div>
          <div className="font-medium text-slate-800 mt-0.5" data-testid="team-recommendation">{teamLabel(rec.team_key)}</div>
          <div className="text-xs text-slate-500 mt-1">{rec.reason}</div>
        </div>
      </div>
      <div className="text-[11px] text-slate-400 mt-2">Context — Country: {ctx.country_of_residence || "—"} · Language: {ctx.preferred_language || "—"} · Citizenship: {ctx.citizenship || "—"}</div>

      {isMgmt ? (
        <div className="flex flex-col sm:flex-row gap-2 mt-3" data-testid="team-reassign-controls">
          <select value={teamKey} onChange={(e) => setTeamKey(e.target.value)} data-testid="team-reassign-select" className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {(data.teams || []).map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="team-reassign-reason" placeholder="Reason for override (required)" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <button onClick={reassign} disabled={busy || teamKey === cur.primary_team_id} data-testid="team-reassign-btn" className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50">{busy && <Icons.Loader2 className="h-4 w-4 animate-spin" />}Reassign</button>
        </div>
      ) : <div className="text-xs text-slate-400 mt-3 flex items-center gap-1"><Icons.Lock className="h-3 w-3" />Only managers and administrators may reassign teams.</div>}

      {showHistory && (
        <div className="mt-3 space-y-1.5 border-t border-slate-200 pt-3" data-testid="team-history">
          {data.history.slice().reverse().map((h, i) => (
            <div key={i} className="text-xs text-slate-600 flex items-start gap-2">
              <Icons.ArrowRight className="h-3 w-3 mt-0.5 text-slate-400" />
              <span><b>{teamLabel(h.from_team)}</b> → <b>{teamLabel(h.to_team)}</b> · {h.source === "auto" ? "auto" : "override"} by {h.by_name} — {h.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Binder({ onRowClick }) {
  useEffect(() => {
    const table = document.querySelector('[data-testid="engagement-letters-table"]');
    if (!table) return;
    const h = (e) => {
      // Ignore clicks on the per-row actions menu trigger so the dropdown doesn't also open the detail
      if (e.target.closest('[data-testid^="el-actions-btn-"]')) return;
      const tr = e.target.closest('[data-testid^="row-"]');
      if (tr) onRowClick(tr.getAttribute("data-testid").replace("row-", ""));
    };
    table.addEventListener("click", h); table.style.cursor = "pointer";
    return () => table.removeEventListener("click", h);
  });
  return null;
}
